import React, { useContext, useState } from 'react';
import { Box, Button, Dialog, DialogTitle, DialogContent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, useTheme, Chip } from '@mui/material';
import { AlertCircle, X } from 'lucide-react';
import { LowStockContext } from '../contexts/LowStockContext';

const LowStockAlertSmall = () => {
  const { lowStockItems, loading } = useContext(LowStockContext);
  const [openModal, setOpenModal] = useState(false);
  const theme = useTheme();

  // Don't show if no low stock items or still loading
  if (loading || lowStockItems.length === 0) {
    return null;
  }

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <>
      {/* Small Alert Message */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          backgroundColor: theme.palette.mode === 'dark' ? '#2a1a1a' : '#fff5f5',
          border: `1px solid ${theme.palette.mode === 'dark' ? '#8b1428' : '#f5d5d5'}`,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <AlertCircle size={18} color="#c41e3a" style={{ flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" fontWeight={600} color="#c41e3a">
            Low Stock Alert: {lowStockItems.length} item{lowStockItems.length !== 1 ? 's' : ''} low in stock
          </Typography>
        </Box>
        <Button
          size="small"
          variant="text"
          onClick={handleOpenModal}
          sx={{
            color: 'error.main',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(196, 30, 58, 0.1)' : 'rgba(196, 30, 58, 0.05)',
            }
          }}
        >
          Show All
        </Button>
      </Box>

      {/* Modal with Detailed Low Stock Items */}
      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 2,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertCircle size={20} color="#c41e3a" />
            <Typography variant="h6" fontWeight={700}>
              Low Stock Items
            </Typography>
          </Box>
          <Button
            onClick={handleCloseModal}
            sx={{
              minWidth: 'auto',
              p: 0.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.action.hover,
              }
            }}
          >
            <X size={20} />
          </Button>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <TableContainer component={Paper} sx={{ boxShadow: 'none', border: `1px solid ${theme.palette.divider}` }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.text.primary }}>Product Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: theme.palette.text.primary }}>Category</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>Current Stock</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lowStockItems.map((item) => (
                  <TableRow key={item.id} sx={{ '&:hover': { backgroundColor: theme.palette.action.hover } }}>
                    <TableCell sx={{ fontWeight: 500 }}>{item.name}</TableCell>
                    <TableCell>{item.category || 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600} color="#c41e3a">
                        {item.stock} units
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label="Low Stock"
                        size="small"
                        sx={{
                          backgroundColor: 'error.lighter',
                          color: 'error.main',
                          fontWeight: 600,
                          border: '1px solid',
                          borderColor: 'error.light',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Items with stock ≤ 5 units are shown above. Consider placing new purchase orders.
          </Typography>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LowStockAlertSmall;
